import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { catchError, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { Employee } from 'src/app/employee/models/Employee';
import { EmployeeService } from 'src/app/employee/services/employee.service';
import { EntityUpdateRequest } from 'src/app/entity_update_request/state/entity_update_request.model';
import { EntityUpdateRequestService } from 'src/app/entity_update_request/state/entity_update_request.service';
import { AvailableWidgets } from '../../../../setting/models/AvailableWidgets';
import { CompanyService } from '../../../../setting/services/company.service';
import { SummernoteHelper } from '../../../../shared/common/SummernoteHelper';
import { EmployeeActivity } from '../../../models/EmployeeActivity';
import { EmployeeRequiredDataUpdates } from '../../../models/EmployeeRequiredDataUpdates';

@Component({
    selector: 'app-employee-overview',
    templateUrl: './employee-overview.component.html',
    styleUrls: ['./employee-overview.component.css']
})
export class EmployeeOverviewComponent implements OnInit, OnDestroy {
    public employee$: Observable<Employee | null>;
    public employee: Employee;
    public availableWidgets$: Observable<AvailableWidgets>;
    public pinnedActivities$: Observable<EmployeeActivity[]>;
    public requiredUpdates: EmployeeRequiredDataUpdates;
    public entityRequests: Array<EntityUpdateRequest>;
    public permissions: Array<string>;
    public watchingMyself = false;
    public profileLoaded = false;
    public expandedActivities = [];
    public employeeID: number;
    public fetchEmployeeExtended$ = new ReplaySubject<void>(1);

    private _subscription: Subscription;

    public constructor(
        public summernoteHelper: SummernoteHelper,
        private _authService: AuthenticationService,
        private _employeeService: EmployeeService,
        private _companyService: CompanyService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _location: Location,
        private _entityUpdateRequestService: EntityUpdateRequestService,
        private _cdr: ChangeDetectorRef,
    ) { }

    public ngOnDestroy(): void {
        this._subscription?.unsubscribe();
    }

    public ngOnInit(): void {
        this.permissions = this._authService?.permissions?.map(perm => perm.name);

        this.availableWidgets$ = this._companyService.availableWidgets$;

        // on employeeID in rout change
        this._subscription = this._route.parent.params.subscribe(params => {
            this.employeeID = parseInt(params.id, 10);
            this.profileLoaded = false;
            const loggedEmployeeID = this._authService?.employee?.employee_ID;
            this.watchingMyself = loggedEmployeeID === this.employeeID;
            if (this.watchingMyself) {
                this.getRequiredDataUpdates(this.employeeID, true);
                this.getActiveRequests(this.employeeID);
            }

            this.pinnedActivities$ = this._employeeService.getEmployeePinnedAcitivty(this.employeeID);

            this._cdr.detectChanges();

            this.fetchEmployeeExtended$.next();
        });

        this.employee$ = this.fetchEmployeeExtended$
            .pipe(
                switchMap(() => this._employeeService.getEmployeeInfoByEmployeeID(this.employeeID, 'extend')),
                tap(employee => {
                    this.employee = employee;
                    this.profileLoaded = true;
                }),
                catchError(error => {
                    if (error.status === 404) {
                        this._router.navigateByUrl('/404').then(() => { });
                    }

                    return of(null);
                }),
                shareReplay()
            );

        this.fetchEmployeeExtended$.next();
    }

    public getActiveRequests(employeeID: number): void {
        this._entityUpdateRequestService.getAllRequests(employeeID, ['NEW'])
            .subscribe((sub: any) => {
                this.entityRequests = sub.data;
            });
    }

    public getRequiredDataUpdates(employeeID: number, scroll?: boolean): void {
        this._employeeService.getRequiredDataUpdates(employeeID)
            .subscribe(requiredUpdates => {
                this.requiredUpdates = requiredUpdates;
                if (scroll) {
                    this._route.fragment.subscribe(fragment => {
                        if (fragment) {
                            const checkInterval = setInterval(() => {
                                const element = document.getElementById(fragment);
                                if (element) {
                                    clearInterval(checkInterval);
                                    const headerOffset = 70;
                                    const elementPosition = element.getBoundingClientRect().top;
                                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                                    window.scrollTo({
                                        top: offsetPosition,
                                        behavior: 'smooth'
                                    });
                                    this._location.replaceState(this._location.path(false));
                                }
                            }, 10);
                        }
                    });
                }
            });
    }

    public checkPermission(name: string, action = 'view'): boolean {
        if (action === 'view') {
            return (this.watchingMyself || this.permissions.includes(name + '.view') || this.permissions.includes(name + '.viewStructure') || this.permissions.includes(name + '.read'));
        }
        if (action === 'edit') {
            return (this.watchingMyself || this.permissions.includes(name + '.edit') || this.permissions.includes(name + '.editStructure'));
        }
    }

    public canShowBoth(availableWidgets: any): boolean {
        return availableWidgets?.contact_persons && availableWidgets?.address && this.checkPermission('employeeAddress') && this.checkPermission('employeeContactPerson');
    }

    public canShowSome(availableWidgets: any): boolean {
        return (availableWidgets?.contact_persons && this.checkPermission('employeeContactPerson')) || (availableWidgets?.address && this.checkPermission('employeeAddress'));
    }
}
